"use client";
import React, { useState, useMemo, useEffect } from "react";
import { Input, Button, Switch, Avatar, Form } from "antd";
import { UserOutlined } from "@ant-design/icons";
import PhoneInput from "react-phone-input-2";
import useAuthSession from "../../authPatient/hooks/useAuthSession";
import { useUserInformation } from "../../Profile/hooks/useUserInformation";
import AppLayout from "../../navigation/Layout/Layout";
import "react-phone-input-2/lib/style.css";
import "./Settings.css";
import { useTranslation } from "react-i18next";

export default function Settings() {
  const [form] = Form.useForm();

  const { t } = useTranslation();

  /** ✅ Load session (like Header) */
  const { auth, effectiveUser } = useAuthSession({ auto: true });
  const user = effectiveUser || auth?.user;

  /** ✅ Hook for updating info + avatar */
  const { handleSubmit, avatarSrc } = useUserInformation(user);

  /** ✅ Notification states */
  const [appointmentReminders, setAppointmentReminders] = useState(true);
  const [medicationReminders, setMedicationReminders] = useState(true);
  const [messageAlerts, setMessageAlerts] = useState(true);
  const [questionUpdates, setQuestionUpdates] = useState(true);
  const [useLocation, setUseLocation] = useState(true);

  /** ✅ Compute initial form values */
  const initial = useMemo(
    () => ({
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
      email: user?.email || "",
      phone: user?.phone || "",
      birthdate: user?.birthdate
        ? new Date(user.birthdate).toISOString().split("T")[0]
        : "",
    }),
    [user]
  );

  /** ✅ Sync form once user data is ready */
  useEffect(() => {
    if (user) form.setFieldsValue(initial);
  }, [user, form, initial]);

  /** ✅ Submit handler */
  const handleFinish = async (values) => {
    const payload = {
      ...values,
      birthdate: values.birthdate,
      phone: values.phone,
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
    };
    await handleSubmit(payload);
  };

  // ✅ Avatar par défaut comme SubMenu
  const avatarToShow =
    avatarSrc ||
    user?.avatarUrl ||
    "https://cdn-icons-png.flaticon.com/512/149/149071.png";

  if (!user)
    return (
      <AppLayout>
        <div className="settings-page">
          <div style={{ textAlign: "center", padding: "4rem" }}>
            {t("commonn.loading")}
          </div>
        </div>
      </AppLayout>
    );

  return (
    <AppLayout>
      <div className="settings-page">
        <div className="settings-scrollable-content">
          {/* ✅ HEADER */}
          <div className="settings-profile-header">
            <Avatar
              size={64}
              icon={<UserOutlined />}
              src={avatarToShow}
              style={{ objectFit: "cover" }}
            />
            <div className="settings-profile-info">
              <h2>
                {user?.firstName && user?.lastName
                  ? `${user.firstName} ${user.lastName}`
                  : t("settings.userNameFallback")}
              </h2>
              <p>{user?.email || t("settings.emailFallback")}</p>
            </div>
            <Button type="primary" className="edit-picture-btn">
              {t("settings.editProfilePicture")}
            </Button>
          </div>

          {/* ✅ PROFILE INFO */}
          <div className="settings-card">
            <div className="settings-card-header">
              <UserOutlined className="settings-icon" />
              <div>
                <h3>{t("settings.profileInfoTitle")}</h3>
                <p>{t("settings.profileInfoSubtitlePatient")}</p>
              </div>
            </div>

            <div className="settings-card-content">
              <Form
                form={form}
                layout="vertical"
                onFinish={handleFinish}
                className="settings-form"
              >
                <div className="settings-form-grid">
                  <div className="settings-field">
                    <label>{t("commonn.firstName")}</label>
                    <Form.Item name="firstName" noStyle>
                      <Input placeholder={t("signup.firstNamePlaceholder")} />
                    </Form.Item>
                  </div>

                  <div className="settings-field">
                    <label>{t("commonn.lastName")}</label>
                    <Form.Item name="lastName" noStyle>
                      <Input placeholder={t("signup.lastNamePlaceholder")} />
                    </Form.Item>
                  </div>

                  <div className="settings-field">
                    <label>{t("commonn.email")}</label>
                    <Form.Item name="email" noStyle>
                      <Input placeholder={t("commonn.emailPlaceholder")} />
                    </Form.Item>
                  </div>

                  <div className="settings-field">
                    <label>{t("signup.dateOfBirth")}</label>
                    <Form.Item name="birthdate" noStyle>
                      <input type="date" className="settings-date-input" />
                    </Form.Item>
                  </div>

                  <div className="settings-field">
                    <label>{t("commonn.phone")}</label>
                    <Form.Item name="phone" noStyle>
                      <PhoneInput
                        country={"tn"}
                        enableSearch={true}
                        inputClass="settings-phone-input"
                        containerClass="settings-phone-container"
                        buttonClass="settings-phone-button"
                        inputStyle={{ width: "100%" }}
                        specialLabel=""
                      />
                    </Form.Item>
                  </div>
                </div>

                <div className="settings-checkbox">
                  <label htmlFor="terms">
                    <input type="checkbox" id="terms" />
                    {" "}
                    {t("signup.agreeToTerms")} {" "}
                    <a href="#">
                      {t("signup.termsAndConditions")} {t("commonn.and")} {" "}
                      {t("signup.privacyPolicy")}
                    </a>
                    .
                  </label>
                  <Button
                    type="primary"
                    onClick={() => form.submit()}
                    className="update-info-btn"
                  >
                    {t("settings.updateInformation")}
                  </Button>
                </div>
              </Form>
            </div>
          </div>

          {/* ✅ NOTIFICATIONS */}
          <div className="settings-card">
            <div className="settings-card-header">
              <span className="settings-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <path
                    d="M6.8457 14C6.96273 14.2027 7.13105 14.371 7.33373 14.488C7.53642 14.605 7.76633 14.6666 8.00037 14.6666C8.23441 14.6666 8.46432 14.605 8.66701 14.488C8.86969 14.371 9.03801 14.2027 9.15504 14"
                    stroke="black"
                    strokeWidth="0.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M2.17418 10.217C2.08709 10.3125 2.02962 10.4312 2.00875 10.5587C1.98788 10.6862 2.00453 10.817 2.05665 10.9353C2.10878 11.0535 2.19414 11.154 2.30235 11.2247C2.41056 11.2953 2.53697 11.3329 2.66618 11.333H13.3328C13.462 11.3331 13.5885 11.2956 13.6968 11.2251C13.805 11.1546 13.8905 11.0542 13.9428 10.936C13.995 10.8179 14.0118 10.6871 13.9911 10.5595C13.9704 10.432 13.9131 10.3132 13.8262 10.2177C12.9395 9.30367 11.9995 8.33234 11.9995 5.33301C11.9995 4.27214 11.5781 3.25473 10.8279 2.50458C10.0778 1.75444 9.06038 1.33301 7.99951 1.33301C6.93865 1.33301 5.92123 1.75444 5.17109 2.50458C4.42094 3.25473 3.99951 4.27214 3.99951 5.33301C3.99951 8.33234 3.05885 9.30367 2.17418 10.217Z"
                    stroke="black"
                    strokeWidth="0.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <div>
                <h3>{t("settings.notificationsTitle")}</h3>
                <p>{t("settings.notificationsSubtitlePatient")}</p>
              </div>
            </div>

            <div className="settings-card-content">
              {[
                {
                  title: t("settings.appointmentReminders"),
                  desc: t("settings.appointmentRemindersDesc"),
                  state: appointmentReminders,
                  setState: setAppointmentReminders,
                },
                {
                  title: t("settings.medicationReminders"),
                  desc: t("settings.medicationRemindersDesc"),
                  state: medicationReminders,
                  setState: setMedicationReminders,
                },
                {
                  title: t("settings.messageAlerts"),
                  desc: t("settings.messageAlertsDesc"),
                  state: messageAlerts,
                  setState: setMessageAlerts,
                },
                {
                  title: t("settings.questionUpdates"),
                  desc: t("settings.questionUpdatesDesc"),
                  state: questionUpdates,
                  setState: setQuestionUpdates,
                },
                {
                  title: t("settings.useLocationTitle"),
                  desc: t("settings.useLocationDesc"),
                  state: useLocation,
                  setState: setUseLocation,
                },
              ].map((item, index) => (
                <div className="settings-notification-item" key={index}>
                  <div className="settings-notification-info">
                    <h4>{item.title}</h4>
                    <p>{item.desc}</p>
                  </div>
                  <Switch checked={item.state} onChange={item.setState} />
                </div>
              ))}
            </div>
          </div>

          {/* ✅ PASSWORD */}
          <div className="settings-card">
            <div className="settings-card-header">
              <span className="settings-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <path
                    d="M13.3327 8.66664C13.3327 12 10.9993 13.6666 8.22602 14.6333C8.08079 14.6825 7.92304 14.6802 7.77935 14.6266C4.99935 13.6666 2.66602 12 2.66602 8.66664V3.99997C2.66602 3.82316 2.73625 3.65359 2.86128 3.52857C2.9863 3.40355 3.15587 3.33331 3.33268 3.33331C4.66602 3.33331 6.33268 2.53331 7.49268 1.51997C7.63392 1.39931 7.81358 1.33301 7.99935 1.33301C8.18511 1.33301 8.36478 1.39931 8.50602 1.51997C9.67268 2.53997 11.3327 3.33331 12.666 3.33331C12.8428 3.33331 13.0124 3.40355 13.1374 3.52857C13.2624 3.65359 13.3327 3.82316 13.3327 3.99997V8.66664Z"
                    stroke="black"
                    strokeWidth="0.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M6 8.00033L7.33333 9.33366L10 6.66699"
                    stroke="black"
                    strokeWidth="0.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <div>
                <h3>{t("settings.changePasswordTitle")}</h3>
                <p>{t("settings.changePasswordSubtitle")}</p>
              </div>
            </div>

            <div className="settings-card-content">
              <div className="settings-password-form">
                <div className="settings-field">
                  <label>{t("settings.currentPassword")}</label>
                  <Input.Password
                    placeholder={t("settings.currentPasswordPlaceholder")}
                  />
                </div>
                <div className="settings-field">
                  <label>{t("settings.newPassword")}</label>
                  <Input.Password
                    placeholder={t("settings.newPasswordPlaceholder")}
                  />
                </div>
                <div className="settings-field">
                  <label>{t("settings.confirmNewPassword")}</label>
                  <Input.Password
                    placeholder={t("settings.confirmNewPasswordPlaceholder")}
                  />
                </div>
                <div className="settings-forgot-link">
                  <a href="#">{t("settings.forgotPasswordHelp")}</a>
                  <Button type="primary">{t("settings.updatePassword")}</Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}

